import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../components/context/AuthProvider";
import { Button, Input } from "../../components/ui";
import Alert from "../../components/ui/Alert/Alert";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notVerified, setNotVerified] = React.useState(false);

  // Extract redirect URL from query parameters
  const redirectPath = new URLSearchParams(location.search).get("redirect") || "/protected/home";
  const redirect = new URLSearchParams(location.search).get("redirect");
  const registerHref = redirect
    ? `/register?redirect=${encodeURIComponent(redirect)}`
    : "/register";

  const form = useFormValidation<LoginForm>({
    initialValues: {
      username: "",
      password: "",
    },
    validationRules: {
      username: commonValidationRules.required,
      password: commonValidationRules.required,
    },
    onSubmit: async (values) => {
      setNotVerified(false);
      let response: Response;
      try {
        response = await fetch("/api/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        });
      } catch {
        // Only network/transport failures reach here — give a friendly message.
        throw new Error("Something went wrong. Please try again later.");
      }

      if (response.ok) {
        const data = await response.json();
        login(data.token, data.refresh_token ?? "");
        navigate(redirectPath); // Redirect to the specified path or default home
        return;
      }

      let errorMessage = "Invalid username or password.";
      try {
        const errorData = await response.json();
        if (response.status === 403 && errorData.error === "email_not_verified") {
          setNotVerified(true);
          return;
        }
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If we can't parse JSON, it might be an HTML error page
        const textResponse = await response.text();
        if (textResponse.includes("502 Bad Gateway")) {
          errorMessage = "Server temporarily unavailable. Please try again.";
        } else if (textResponse.includes("nginx")) {
          errorMessage = "Connection error. Please check your internet connection.";
        }
      }
      throw new Error(errorMessage);
    },
  });

  return (
    <div className="bg-zinc-950 text-white min-h-screen flex flex-col">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-8 py-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm">
        <a href="/" className="flex items-center gap-3 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-8 h-8 object-contain" />
          <span className="text-xl font-light tracking-widest">
            Role<span className="text-amber-400 font-normal">Weave</span>
          </span>
        </a>
        <nav className="flex items-center gap-6">
          <a
            href={registerHref}
            className="px-5 py-2 border border-amber-400/50 text-amber-400 text-sm tracking-wide hover:bg-amber-400 hover:text-zinc-950 transition-all duration-200"
          >
            Get started
          </a>
        </nav>
      </header>

      {/* Form */}
      <main className="flex-1 flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
            Welcome back
          </p>
          <h1 className="text-3xl md:text-4xl font-thin text-center text-white mb-10 tracking-wide">
            Sign in
          </h1>

          <div className="border border-zinc-800 bg-zinc-900 p-8">
            {notVerified && (
              <div className="mb-4">
                <Alert variant="warning">
                  Please verify your email before signing in. Check your inbox or{" "}
                  <Link
                    to="/resend-verification"
                    className="underline hover:text-amber-300 transition-colors duration-200"
                  >
                    resend the verification email
                  </Link>
                  .
                </Alert>
              </div>
            )}
            <form onSubmit={form.handleSubmit}>
              <div className="mb-4">
                <Input
                  type="text"
                  id="username"
                  label="Username"
                  value={form.values.username}
                  onChange={form.handleChange("username")}
                  onBlur={form.handleBlur("username")}
                  error={form.errors.username}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-2">
                <Input
                  type="password"
                  id="password"
                  label="Password"
                  value={form.values.password}
                  onChange={form.handleChange("password")}
                  onBlur={form.handleBlur("password")}
                  error={form.errors.password}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-6 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-400 hover:text-amber-400 transition-colors duration-200"
                >
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={form.isSubmitting}
              >
                {form.isSubmitting ? "Signing in..." : "Sign in"}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-zinc-400">
              Don&apos;t have an account?{" "}
              <Link
                to={registerHref}
                className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
              >
                Get started
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 px-8 border-t border-zinc-900 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-5 h-5 object-contain opacity-70" />
          <span className="text-zinc-500 text-xs tracking-widest">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-zinc-500 text-xs">© 2026 RoleWeave. All rights reserved.</p>
        <div className="flex gap-6">
          <a
            href="/login"
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
          >
            Sign in
          </a>
          <a
            href="/register"
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
          >
            Register
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Login;
